// ** Document API Integration for Quiz File Upload
// ** This utility provides integration between the quiz system and document management

import { toast } from 'react-toastify';

class DocumentAPIIntegration {
  constructor() {
    this.uploadDocument = this.uploadDocument.bind(this);
    this.getDocument = this.getDocument.bind(this);
    this.deleteDocument = this.deleteDocument.bind(this);
  }

  /**
   * Upload a document using the document management system
   * @param {File} file - The file to upload
   * @param {string} documentType - Type of document (W2, 1099, etc.)
   * @param {string} taxFileId - Associated tax file ID
   * @returns {Promise<Object>} Upload result
   */
  async uploadDocument(file, documentType, taxFileId) {
    try {
      // Validate inputs
      if (!file) {
        throw new Error('File is required');
      }
      if (!documentType) {
        throw new Error('Document type is required');
      }
      if (!taxFileId) {
        throw new Error('Tax file ID is required');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('document', file);

      // Get auth token from localStorage or Redux store
      const token =
        localStorage.getItem('token') ||
        window.store?.getState?.()?.users?.token;

      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Make API call
      const response = await fetch(
        `/api/v1/documents/upload?documentType=${documentType}&taxFileId=${taxFileId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            // Note: Don't set Content-Type for FormData, browser will set it with boundary
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Document upload failed');
      }

      // Show success notification
      toast.success('Document uploaded successfully');

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error('Document upload error:', error);
      toast.error(error.message || 'Failed to upload document');

      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get a document URL for viewing
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Document URL result
   */
  async getDocument(documentId) {
    try {
      if (!documentId) {
        throw new Error('Document ID is required');
      }

      const token =
        localStorage.getItem('token') ||
        window.store?.getState?.()?.users?.token;

      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`/api/v1/documents/${documentId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to retrieve document');
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error('Document retrieval error:', error);
      toast.error(error.message || 'Failed to retrieve document');

      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Delete a document
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteDocument(documentId) {
    try {
      if (!documentId) {
        throw new Error('Document ID is required');
      }

      const token =
        localStorage.getItem('token') ||
        window.store?.getState?.()?.users?.token;

      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`/api/v1/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete document');
      }

      toast.success('Document deleted successfully');

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error('Document deletion error:', error);
      toast.error(error.message || 'Failed to delete document');

      return {
        success: false,
        error: error.message,
      };
    }
  }
}

// Create singleton instance
const documentAPI = new DocumentAPIIntegration();

// Make it globally available
if (typeof window !== 'undefined') {
  window.DocumentUploadAPI = documentAPI;
}

export default documentAPI;
